/**
 * 真人出镜页三条入参通道(0924 车 D 二期,契约 §3.4)—— 读 URL / location.state,
 * 取回模板与草稿,各**只交付一次**给页面(页面决定怎么填,覆盖纪律在页面侧)。
 *
 *   `?templateId=`             → GET /video/templates/{id} → onTemplate(t)(只认 REAL_PERSON 类型)
 *   `?draft=`                  → GET /video/real/script/{id} → onDraft(d)
 *   `location.state.explosive` → parseRealPersonHandoff(零逐字投影再过一遍)→ onExplosive(h)
 *
 * 此前这页是孤岛(不读 query、不读 state),全站唯一入口是视频舱一张无参卡片。
 */
import { useEffect, useMemo, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { parseRealPersonHandoff, type RealPersonExplosiveHandoff } from "@/entities/explosive";
import { toast } from "@/shared/hooks/use-toast";
import {
  REAL_PERSON_TEMPLATE_TYPE,
  videoService,
  type RealScriptDraftDto,
  type VideoTemplateDto,
} from "../../../api/videoService";
import { parseRealPersonQuery } from "../../../model/realPersonIntake";

export function useRealPersonIntake(handlers: {
  onTemplate: (t: VideoTemplateDto) => void;
  onDraft: (d: RealScriptDraftDto) => void;
  onExplosive: (h: RealPersonExplosiveHandoff) => void;
}) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { templateId, draftId } = useMemo(() => parseRealPersonQuery(searchParams), [searchParams]);
  const explosive = useMemo(() => parseRealPersonHandoff(location.state), [location.state]);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  // 每个来源只交付一次(按 id 记),避免 re-render / refetch 反复覆盖用户已改的内容。
  const delivered = useRef<{ tpl: number | null; draft: string | null; exp: number | null }>({
    tpl: null,
    draft: null,
    exp: null,
  });

  const tplQuery = useQuery({
    queryKey: ["video", "template", templateId],
    queryFn: () => videoService.getTemplate(templateId as number),
    enabled: templateId != null,
    meta: { suppressGlobalErrorToast: true },
  });
  const draftQuery = useQuery({
    queryKey: ["video", "real-script-draft", draftId],
    queryFn: () => videoService.getRealScriptDraft(draftId as string),
    enabled: draftId != null,
    retry: false,
    meta: { suppressGlobalErrorToast: true },
  });

  useEffect(() => {
    const t = tplQuery.data;
    if (!t || templateId == null || delivered.current.tpl === templateId) return;
    delivered.current.tpl = templateId;
    if (t.type !== REAL_PERSON_TEMPLATE_TYPE) {
      toast({
        title: "这个模板不是真人出镜样片",
        description: "已忽略,可以在下方样片墙重新挑一条",
      });
      return;
    }
    handlersRef.current.onTemplate(t);
  }, [tplQuery.data, templateId]);

  useEffect(() => {
    if (tplQuery.isError && templateId != null && delivered.current.tpl !== templateId) {
      delivered.current.tpl = templateId;
      toast({ title: "样片没找到", description: "可能已下架,在样片墙重新挑一条吧" });
    }
  }, [tplQuery.isError, templateId]);

  useEffect(() => {
    const d = draftQuery.data;
    if (!d || draftId == null || delivered.current.draft === draftId) return;
    delivered.current.draft = draftId;
    handlersRef.current.onDraft(d);
  }, [draftQuery.data, draftId]);

  useEffect(() => {
    if (draftQuery.isError && draftId != null && delivered.current.draft !== draftId) {
      delivered.current.draft = draftId;
      toast({
        title: "脚本草稿找不到了",
        description: "草稿只保留 24 小时,可以点「帮我写脚本」重新写一份",
      });
    }
  }, [draftQuery.isError, draftId]);

  useEffect(() => {
    if (!explosive || delivered.current.exp === explosive.blueprintId) return;
    delivered.current.exp = explosive.blueprintId;
    handlersRef.current.onExplosive(explosive);
  }, [explosive]);

  return { explosive, loadingDraft: draftQuery.isLoading && draftId != null };
}
